import type { AgentTaskModel } from "@fencyai/js";
import { useAgentTasks } from "@fencyai/react";
import { useCallback, useState } from "react";
import { isFetchBaseQueryError } from "../errors/isFetchBaseQueryError";
import { useExploreConversation } from "./useExploreConversation";

type UseExploreMemoriesChatProps = {
  model: AgentTaskModel;
  fetchCreateExploreMemoriesClientToken: (conversationId: string) => Promise<{ clientToken: string }>;
  fetchFencyFileDownloadClientToken: (agentTaskFileId: string) => Promise<{ clientToken: string }>;
};

export const useExploreMemoriesChat = ({
  model,
  fetchCreateExploreMemoriesClientToken,
  fetchFencyFileDownloadClientToken,
}: UseExploreMemoriesChatProps) => {
  const [prompt, setPrompt] = useState("");
  const [userMessages, setUserMessages] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { ensureConversation, invalidateConversation } = useExploreConversation();

  const fetchExploreMemoriesClientToken = useCallback(
    async (conversationId: string) => {
      try {
        return await fetchCreateExploreMemoriesClientToken(conversationId);
      } catch (error) {
        if (isFetchBaseQueryError(error) && error.status === 403) {
          invalidateConversation();
        }
        throw error;
      }
    },
    [fetchCreateExploreMemoriesClientToken, invalidateConversation]
  );

  const { agentTasks, createAgentTask } = useAgentTasks({
    fetchCreateAgentTaskClientToken: async () => {
      const id = await ensureConversation();
      return fetchExploreMemoriesClientToken(id);
    },
  });

  const handleSend = useCallback(async () => {
    const trimmed = prompt.trim();
    if (!trimmed || isLoading) {
      return;
    }

    setUserMessages((prev) => [...prev, trimmed]);
    setPrompt("");
    setIsLoading(true);

    try {
      const conversationId = await ensureConversation();
      const result = await createAgentTask(
        {
          type: "ExploreMemories",
          query: trimmed,
          model,
          language: "no",
        },
        {
          fetchCreateAgentTaskClientToken: () => fetchExploreMemoriesClientToken(conversationId),
          fetchAgentTaskFileDownloadClientToken: async (agentTaskFileId) =>
            fetchFencyFileDownloadClientToken(agentTaskFileId),
        }
      );

      if (result.type !== "success") {
        setUserMessages((prev) => prev.slice(0, -1));
        setPrompt(trimmed);
      }
    } catch {
      setUserMessages((prev) => prev.slice(0, -1));
      setPrompt(trimmed);
    } finally {
      setIsLoading(false);
    }
  }, [
    createAgentTask,
    ensureConversation,
    fetchExploreMemoriesClientToken,
    fetchFencyFileDownloadClientToken,
    isLoading,
    model,
    prompt,
  ]);

  return {
    userMessages,
    agentTasks,
    prompt,
    setPrompt,
    isLoading,
    handleSend,
  };
};
